import {
	MultipleChoice,
	SwitchComponent,
	TextInputField,
} from '@monthio/react-components'
import { t } from 'i18next'
import React, { VoidFunctionComponent } from 'react'
import styled from 'styled-components'
import { CustomBankProfile } from './CustomProfileCreation'

const CustomBankProfileCreationContainer = styled.div`
	display: flex;
	flex-flow: column nowrap;

	> div:not(:first-child) {
		margin-top: 8px;
	}
`
const TextInput = styled(TextInputField)`
	margin: 5px;
`
const ElementHeader = styled.span`
	margin-top: 15px;
	font-size: 16px;
	font-weight: 800;
`
const SectionHeader = styled.h2`
	font-size: 18px;
	font-weight: 1000;
	line-height: 24px;
	text-decoration: underline;
	margin-top: 0;
	&:not(:first-of-type) {
		margin-top: 35px;
	}
`

const InputRowWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 10px;
`

interface CustomBankAccountProps {
	customProfileInputs: CustomBankProfile
	updateProfile: (
		key: keyof CustomBankProfile,
		value: string | number | boolean,
	) => void
}

export const CustomBankAccount: VoidFunctionComponent<
	CustomBankAccountProps
> = ({ customProfileInputs, updateProfile }) => {
	const inputHandler =
		(
			input: keyof Pick<
				CustomBankProfile,
				| 'income'
				| 'transport'
				| 'loans'
				| 'housing'
				| 'others'
				| 'nrExtraTransactions'
				| 'ingoingForeignTransfers'
				| 'outgoingForeignTransfers'
				| 'cashWithdrawals'
			>,
		) =>
		(e: any) =>
			updateProfile(input, parseInt(e.target.value))

	return (
		<CustomBankProfileCreationContainer>
			<SectionHeader>
				{t('customProfileCreation-sectionHeader-income')}
			</SectionHeader>
			<TextInput
				label={t('customProfileCreation-label-income')}
				variant="outlined"
				onChange={inputHandler('income')}
				defaultValue={customProfileInputs.income}
				type="number"
			/>
			<ElementHeader>
				{t('customProfileCreation-incomeVariability-title')}
			</ElementHeader>
			<MultipleChoice
				fieldName="incomeVariability"
				choices={[
					{
						label: t('customProfileCreation-incomeVariability-none'),
						value: 'none',
					},
					{
						label: t('customProfileCreation-incomeVariability-low'),
						value: 'low',
					},
					{
						label: t('customProfileCreation-incomeVariability-medium'),
						value: 'medium',
					},
					{
						label: t('customProfileCreation-incomeVariability-high'),
						value: 'high',
					},
				]}
				selected={customProfileInputs.incomeVariability}
				onClick={(event: any) => {
					updateProfile('incomeVariability', event.target.value)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-twoIncomeStreams')}
				id="twoIncomeStreams"
				checked={customProfileInputs.twoIncomeStreams}
				onChange={(_, checked) => {
					updateProfile('twoIncomeStreams', checked)
				}}
			/>
			<SectionHeader>
				{t('customProfileCreation-sectionHeader-housing')}
			</SectionHeader>
			<TextInput
				label={t('customProfileCreation-label-housing')}
				variant="outlined"
				type="number"
				onChange={inputHandler('housing')}
				defaultValue={customProfileInputs.housing}
			/>
			<ElementHeader>
				{t('customProfileCreation-housing-ownerform')}
			</ElementHeader>
			<MultipleChoice
				fieldName="housing"
				choices={[
					{
						label: t('customProfileCreation-housingType-own'),
						value: 'own',
					},
					{
						label: t('customProfileCreation-housingType-rent'),
						value: 'rent',
					},
				]}
				selected={customProfileInputs.housingType}
				onClick={(event: any) => {
					updateProfile('housingType', event.target.value)
				}}
			/>
			<SectionHeader>
				{t('customProfileCreation-sectionHeader-expenses')}
			</SectionHeader>
			<InputRowWrapper>
				<TextInput
					label={t('customProfileCreation-label-transport')}
					variant="outlined"
					type="number"
					onChange={inputHandler('transport')}
					defaultValue={customProfileInputs.transport}
				/>
				<TextInput
					label={t('customProfileCreation-label-loans')}
					variant="outlined"
					type="number"
					onChange={inputHandler('loans')}
					defaultValue={customProfileInputs.loans}
				/>
				<TextInput
					label={t('customProfileCreation-label-others')}
					variant="outlined"
					type="number"
					onChange={inputHandler('others')}
					defaultValue={customProfileInputs.others}
				/>
			</InputRowWrapper>
			<SectionHeader>
				{t('customProfileCreation-sectionHeader-ForeignTransfers')}
			</SectionHeader>
			<InputRowWrapper>
				<TextInput
					label={t('customProfileCreation-ingoing')}
					variant="outlined"
					type="number"
					onChange={inputHandler('ingoingForeignTransfers')}
					defaultValue={customProfileInputs.ingoingForeignTransfers}
				/>
				<TextInput
					label={t('customProfileCreation-outgoing')}
					variant="outlined"
					type="number"
					onChange={inputHandler('outgoingForeignTransfers')}
					defaultValue={customProfileInputs.outgoingForeignTransfers}
				/>
			</InputRowWrapper>
			<SectionHeader>
				{t('customProfileCreation-sectionHeader-otherAttributes')}
			</SectionHeader>
			<InputRowWrapper>
				<TextInput
					label={t('customProfileCreation-label-nrExtraTransactions')}
					variant="outlined"
					type="number"
					onChange={inputHandler('nrExtraTransactions')}
					defaultValue={customProfileInputs.nrExtraTransactions}
				/>
				<TextInput
					label={t('customProfileCreation-label-cashWithdrawals')}
					variant="outlined"
					type="number"
					onChange={inputHandler('cashWithdrawals')}
					defaultValue={customProfileInputs.cashWithdrawals}
				/>
			</InputRowWrapper>
			<SwitchComponent
				label={t('customProfileCreation-label-gambling')}
				id="gambling"
				checked={customProfileInputs.gambling}
				onChange={(_, checked) => {
					updateProfile('gambling', checked)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-childIndicator')}
				id="childIndicator"
				checked={customProfileInputs.childIndicator}
				onChange={(_, checked) => {
					updateProfile('childIndicator', checked)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-debtCollection')}
				id="debtCollection"
				checked={customProfileInputs.debtCollection}
				onChange={(_, checked) => {
					updateProfile('debtCollection', checked)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-publicDebtCollection')}
				id="publicDebtCollection"
				checked={customProfileInputs.publicDebtCollection}
				onChange={(_, checked) => {
					updateProfile('publicDebtCollection', checked)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-hasSecondAccount')}
				id="hasSecondAccount"
				checked={customProfileInputs.hasSecondAccount}
				onChange={(_, checked) => {
					updateProfile('hasSecondAccount', checked)
				}}
			/>
			<SwitchComponent
				label={t('customProfileCreation-label-additionalIncome')}
				id="hasAdditionalIncome"
				checked={customProfileInputs.hasAdditionalIncome}
				onChange={(_, checked) => {
					updateProfile('hasAdditionalIncome', checked)
				}}
			/>
		</CustomBankProfileCreationContainer>
	)
}
